/**
 * Página de pedidos con productos en Nave 2
 * Permite recuperar pedidos, ver listado y gestionar estados
 */
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Select,
    MenuItem,
    Button,
    CircularProgress,
    Chip,
    Stack,
    Divider
} from "@mui/material";
import { DataGrid, GridRowEditStopReasons, GridRowModes } from "@mui/x-data-grid";
import { DATA_GRID_LOCALE_TEXT, DATA_GRID_SLOT_PROPS, DATA_GRID_SX } from "../../../components/ui/dataGridStyles";
import RestoreIcon from "@mui/icons-material/Restore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CategoryIcon from "@mui/icons-material/Category";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import SalesService from "../../../services/sales.service";
import Utils from "../../../utils/Utils";
import { generateSalesN2Columns } from "../helpers/columnsSalesN2";
import OrderFactory from "../../../models/OrderFactory";
import OrderService from "../../../services/order.service";
import OrderStates from "../../../utils/OrderStates";
import Toasts from "../../../components/notificacion/Toasts";
import { StateContext } from "../../../components/context/StateProvider";
import withRouter from "../../../components/withRouter";
import VoiceUtils from "../../../utils/VoiceUtils";
import { OrderType, orderTypesWithLabelsSpecial } from "../../../models/Order";
import { BotonPedidosPreparadosN2, FieldLabel, ModalForzarPedidoN2 } from "../components";
import useForzarPedidoN2 from "../hooks/useForzarPedidoN2";
import useProductosPedidosN2 from "../hooks/useProductosPedidosN2";
import EtiquetasPedidoN2 from "../components/ui/EtiquetasPedidoN2";
import { getOrderStateBadgeStyles } from "../utils/orderStateColors";
import { ALTURA_BOTON, BOTON_PRIMARIO_SX } from "../../../components/ui/buttonStyles";


const SalesN2 = (props) => {

    const {state, updateState, getNombreAlmacen} = useContext(StateContext);

    // Datos de la tabla
    const [orders, setOrders] = useState([]);
    const [ordersModels, setOrdersModels] = useState({});
    const [loading, setLoading] = useState(true);

    // Campos de los dos formularios de la cabecera
    const [order, setOrder] = useState('');
    const [orderType, setOrderType] = useState(OrderType.Albaran.valueOf());
    const [qrInput, setQrInput] = useState('');

    // Viewport
    const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 900px)').matches);

    // La recarga periódica vive desde el montaje, así que su closure congelaría el
    // estado inicial. Estas refs replican lo que hacía this.context / this.state en la
    // clase: leer siempre el valor actual. Importan para acumular toasts sin perder los
    // anteriores y para comparar contra los pedidos que hay AHORA en la tabla.
    const estadoRef = useRef(state);
    estadoRef.current = state;
    const ordersRef = useRef(orders);
    ordersRef.current = orders;

    // Productos de cada pedido, para las etiquetas de la tabla y las tarjetas
    const productosPorPedido = useProductosPedidosN2(orders);

    // Forzar pedidos colgados: se quitan de la tabla sin esperar al siguiente refresco
    const forzado = useForzarPedidoN2((pedido) => {
        setOrders((previo) => previo.filter((row) => row.num !== pedido.num));
    });

    /**
     * Obtener albaranes con productos en nave 2
     * @returns {Promise<void>}
     */
    const getData = useCallback(async (checkNewOrders) => {
        const year = estadoRef.current.year;

        const ordersApi = await SalesService.getSalesN2(year);

        if (ordersApi.length > ordersRef.current.length && checkNewOrders) {
            VoiceUtils.speak('Atención! Hay pedidos nuevos');
            const hasRecojoEnAlmacen = ordersApi.some(order => Utils.getTransport(order.TRANSPORTE));
            if (hasRecojoEnAlmacen) {
                VoiceUtils.speak('Atención! Hay pedidos que se recojen en almacén');
            }
        }

        const ordersMap = ordersApi.map(async (order) => {

            let orderN2 = OrderFactory.OrderN2(order.LETRA+'-'+order.NUMERO, new Date(order.FECHA), order.ClientesModel?.NOMBRE, order.ClientesModel?.RutasModel?.NOMBRE, order.TRANSPORTE, false, order.ALMACEN, order.ESTADO_NAVE_1, order.TIPO, order.PREPARADOR_NAVE_1);

            try {
                const orderState = await OrderService.getStateOrderN2(order.LETRA+'-'+order.NUMERO, year, order.TIPO);

                if (orderState.length > 0) {
                    orderN2.preparer = (orderState[0].preparado === null ? '0' : orderState[0].preparado);
                    orderN2.state = (orderState[0].estado === null ? '1' : orderState[0].estado);
                    orderN2.exclude = orderState[0].excluir;

                    if (orderN2.state === 'preparado') {
                        orderN2.complete = true;
                    }
                }

                if (orderN2.state === '1') {
                    if (order.TRANSPORTE === undefined) {
                        orderN2.priority = 2;
                    } else if ( Utils.getTransport(order.TRANSPORTE)) {
                        orderN2.priority = 1;
                    }
                }

            } catch (error) {
                const toasts = Toasts.generateToast(error?.message, 'error');
                updateState({toasts: [...estadoRef.current.toasts, toasts]})
            }


            return orderN2;
        })

        const salesMapFull = await Promise.all(ordersMap);
        setOrders([...salesMapFull].sort((a, b) => (a.priority < b.priority ? 1 : -1)));
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Carga inicial, recarga periódica y listener de viewport. Deps vacías a propósito:
    // conmutar entre tarjetas y tabla no debe volver a pedir los pedidos.
    useEffect(() => {
        getData(false);
        const interval = setInterval(() => getData(true), 30000);

        updateState({filters: [{field: 'state', operator: 'isAnyOf', value: ['1', '2']}]});

        // Listener para detección reactiva de móvil
        const mediaQuery = window.matchMedia('(max-width: 900px)');
        const handleMediaQueryChange = (e) => {
            setIsMobile(e.matches);
        };
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleMediaQueryChange);
        } else {
            mediaQuery.addListener(handleMediaQueryChange);
        }

        return () => {
            clearInterval(interval);
            // Remover listener del media query
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleMediaQueryChange);
            } else {
                mediaQuery.removeListener(handleMediaQueryChange);
            }
            updateState({filters: []});
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (event, type) => {
        if (type === 'order') {
            setOrder(event.target.value);
        } else if (type === 'qrInput') {
            setQrInput(event.target.value);
        } else {
            setOrder('');
            setOrderType(event.target.value);
        }
    };

    const handleSubmit = async (event) => {

        if (event) {
            event.preventDefault();
        }

        if (order !== "") {

            updateState({loading: true});

            await SalesService.reInsertOrderN2(order, estadoRef.current.year, orderType).then(
                async (data) => {
                    let type;
                    if (data.details?.existen) {
                        type = 'success';
                        setLoading(true);
                        setOrder('');
                        getData(false).then(() => {
                            setLoading(false);
                        });

                    } else {
                        type = 'warning';
                    }

                    const toasts = Toasts.generateToast(data.message, type);
                    updateState({toasts: [...estadoRef.current.toasts, toasts]})
                    updateState({loading: false});
                }
            );
        }
    };

    /**
     * Abrir el pedido escaneado por QR.
     * Antes creaba un form con action "#/..." y lo enviaba, pero en un submit GET el
     * navegador descarta el fragmento del action, así que nunca navegaba.
     */
    const handleSubmitForm = (event) => {

        if (event) {
            event.preventDefault();
        }

        const numero = qrInput.trim();

        if (numero === '') {
            return;
        }

        if (!Utils.checkOrder(numero)) {
            const toasts = Toasts.generateToast('Formato de albarán inválido: SERIE-ALBARAN', 'error');
            updateState({toasts: [...estadoRef.current.toasts, toasts]});
            return;
        }

        // DECISION: el QR solo trae el número. El tipo y la ruta se cogen del pedido ya
        // cargado en la tabla, igual que hace su enlace, porque OrderN2 hace
        // .toLowerCase() sobre el tipo y sin ?tipo= la página revienta.
        const pedido = orders.find((orden) => orden.num === numero);
        const tipo = pedido?.type || orderType;
        const ruta = pedido?.route || '';

        props.navigate(
            `/pedidosn2/order/${numero}?tipo=${encodeURIComponent(tipo)}&ruta=${encodeURIComponent(ruta)}`
        );
    };

    const handleRowEditStop = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    };

    const handleRowModesModelChange = (newRowModesModel) => {
        setOrdersModels(newRowModesModel);
    };

    const handleEdit = (id) => {
        setOrdersModels((previo) => ({
            ...previo,
            [id]: {mode: GridRowModes.Edit},
        }));
    };

    const handleSave = (id) => {
        setOrdersModels((previo) => ({...previo, [id]: {mode: GridRowModes.View}}));
    };

    const handleCancel = (id) => {
        setOrdersModels((previo) => ({
            ...previo,
            [id]: {mode: GridRowModes.View, ignoreModifications: true},
        }));

        // La identidad de fila de este grid es `num` (ver getRowId), no `id`: los
        // pedidos de OrderFactory.OrderN2 no llevan id, así que buscar por id
        // devolvía undefined y cancelar la edición reventaba al leer isNew
        const editedRow = ordersRef.current.find((row) => row.num === id);
        if (editedRow?.isNew) {
            setOrders((previo) => previo.filter((row) => row.num !== id));
        }
    };

    const processRowUpdate = async (newRow) => {

        if (!newRow.isNew) {
            OrderService.assignToOrder(newRow.num, null, null, 'ventas', null, true, newRow.exclude, estadoRef.current.year, null, newRow.type, null, null).then(
                () => {
                    const toasts = Toasts.generateToast('Se ha modificado pedido correctamente', 'success');
                    updateState({toasts: [...estadoRef.current.toasts, toasts]})
                }
            ).catch((error) => {
                const toasts = Toasts.generateToast(error.message, 'error');
                updateState({toasts: [...estadoRef.current.toasts, toasts]})
            });
        }

        // Escribir en `orders` y comparar por `num`: las filas de OrderFactory.OrderN2 no
        // llevan `id`, y `deliveries` no existe en este estado. Antes el grid mostraba el
        // cambio por su copia interna pero el estado local no se enteraba.
        const updatedRow = {...newRow, isNew: false};
        setOrders((previo) => previo.map((row) => (row.num === newRow.num ? updatedRow : row)));
        return updatedRow;
    };

    // /**
    //  * Filtrado de lista
    //  * @param value
    //  */
    // filterList(value) {
    //     const {updateState} = this.context;
    //
    //     let optios = [];
    //     Array.from(value).forEach((element) => {
    //         optios.push(element.value);
    //     });
    //
    //     updateState({filters: [{field: 'state', operator: 'isAnyOf', value: optios}]});
    // }

    const columns = generateSalesN2Columns({
        getNombreAlmacen,
        ordersModels,
        onEditar: handleEdit,
        onGuardar: handleSave,
        onCancelar: handleCancel,
        onForzar: forzado.solicitarForzado,
        productosPorPedido,
    });

    return (
            <Box id="sales" sx={{ width: "100%" }}>
                {/* Formularios de recuperar y ver pedido */}
                <Card sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                    <CardContent sx={{ "&:last-child": { pb: { xs: 2, sm: 3 } } }}>
                        {/* Formulario: Recuperar pedido */}
                        <Box
                            component="form"
                            onSubmit={(e) => handleSubmit(e)}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: { xs: 2, sm: 2.5 },
                                mb: 3
                            }}
                        >
                            <Box>
                                <FieldLabel icon={CategoryIcon}>Tipo</FieldLabel>
                                <Select
                                    id="list-order-type"
                                    name="list-order-type"
                                    value={orderType}
                                    onChange={(e) => handleChange(e, "orderType")}
                                    fullWidth
                                    sx={{
                                        backgroundColor: "#fff",
                                        "& .MuiSelect-select": {
                                            py: { xs: 1.5, sm: 1.75 }
                                        }
                                    }}
                                >
                                    {orderTypesWithLabelsSpecial.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>

                            <Box>
                                <FieldLabel icon={RestoreIcon}>Recuperar pedido</FieldLabel>
                                <TextField
                                    type="text"
                                    id="order"
                                    name="order"
                                    value={order}
                                    onChange={(e) => handleChange(e, "order")}
                                    fullWidth
                                    placeholder="Ej: 00-12345"
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: "#fff"
                                        },
                                        "& .MuiInputBase-input": {
                                            py: { xs: 1.5, sm: 1.75 }
                                        }
                                    }}
                                />
                            </Box>

                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={state.loading}
                                startIcon={
                                    state.loading ? (
                                        <CircularProgress size={20} sx={{ color: "#fff" }} />
                                    ) : (
                                        <RestoreIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                    )
                                }
                                sx={{ ...BOTON_PRIMARIO_SX, height: ALTURA_BOTON.FILTRO }}
                            >
                                {state.loading ? "Procesando..." : "Recuperar"}
                            </Button>
                        </Box>

                        {/* Formulario: Ver pedido (QR) */}
                        <Box
                            component="form"
                            onSubmit={(e) => handleSubmitForm(e)}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: { xs: 2, sm: 2.5 }
                            }}
                        >
                            <Box>
                                <FieldLabel icon={VisibilityIcon}>Ver pedido</FieldLabel>
                                <TextField
                                    type="text"
                                    id="qrInput"
                                    name="qrInput"
                                    value={qrInput}
                                    onChange={(e) => handleChange(e, "qrInput")}
                                    fullWidth
                                    placeholder="Escanear QR..."
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: "#fff"
                                        },
                                        "& .MuiInputBase-input": {
                                            py: { xs: 1.5, sm: 1.75 }
                                        }
                                    }}
                                />
                            </Box>

                            <Button type="submit" sx={{ display: "none" }}>
                                Ver
                            </Button>
                        </Box>
                    </CardContent>
                </Card>

                {/* Tabla de pedidos */}
                <Card sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box
                        sx={{
                            px: { xs: 1, sm: 2 },
                            pt: { xs: 2, sm: 3 },
                            pb: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1.5
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: "1.1rem", sm: "1.25rem" },
                                color: "#212529"
                            }}
                        >
                            Pedidos con productos en nave 2
                        </Typography>
                        <BotonPedidosPreparadosN2 />
                    </Box>
                    <CardContent sx={{ "&:last-child": { pb: { xs: 2, sm: 3 } } }}>
                        <>
                                {/* Vista móvil: Cards. El DataGrid de escritorio indica la carga
                                    con su propio overlay, así que aquí hace falta el equivalente */}
                                {isMobile && loading ? (
                                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                                        <CircularProgress sx={{ color: "#cc071e" }} />
                                    </Box>
                                ) : isMobile ? (
                                    <Stack spacing={2}>
                                        {orders.map((order) => {
                                            const getBackgroundColor = () => {
                                                if (order.exclude) return "#ff943975";
                                                if (Utils.getTransport(order.transport)) return "#b8daba";
                                                return "#fff";
                                            };

                                            return (
                                                <Card
                                                    key={order.num}
                                                    sx={{
                                                        backgroundColor: getBackgroundColor(),
                                                        borderRadius: 2,
                                                        border: "1px solid #e9ecef",
                                                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                                                        overflow: "hidden"
                                                    }}
                                                >
                                                    {/* Header */}
                                                    <Box
                                                        sx={{
                                                            p: 2,
                                                            backgroundColor: "#f8f9fa",
                                                            borderBottom: "1px solid #e9ecef"
                                                        }}
                                                    >
                                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                            <Box
                                                                component="a"
                                                                href={`#/pedidosn2/order/${order.num}?tipo=${order.type}&ruta=${order.route}`}
                                                                sx={{
                                                                    textDecoration: "none",
                                                                    color: "#cc071e",
                                                                    fontWeight: 700,
                                                                    fontSize: 16,
                                                                    "&:hover": {
                                                                        textDecoration: "underline"
                                                                    }
                                                                }}
                                                            >
                                                                {order.num}
                                                            </Box>
                                                            <Chip
                                                                label={OrderStates.getState(order.state)}
                                                                size="small"
                                                                sx={getOrderStateBadgeStyles(order.state, { fontSize: 11 })}
                                                            />
                                                        </Stack>
                                                    </Box>

                                                    {/* Contenido */}
                                                    <CardContent sx={{ p: 2 }}>
                                                        <Stack spacing={1.5}>
                                                            {/* Tipo */}
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                <CategoryIcon sx={{ fontSize: 16, color: "#495057" }} />
                                                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6c757d", minWidth: 80 }}>
                                                                    Tipo:
                                                                </Typography>
                                                                <Typography sx={{ fontSize: 13, color: "#212529" }}>
                                                                    {Utils.toCapitalize(order.type)}
                                                                </Typography>
                                                            </Box>

                                                            {/* Fecha */}
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                <CalendarTodayIcon sx={{ fontSize: 16, color: "#495057" }} />
                                                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6c757d", minWidth: 80 }}>
                                                                    Fecha:
                                                                </Typography>
                                                                <Typography sx={{ fontSize: 13, color: "#212529" }}>
                                                                    {Utils.formatDateWithoutHour(order.date)}
                                                                </Typography>
                                                            </Box>

                                                            {/* Cliente */}
                                                            {order.customer && (
                                                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                                                    <PersonIcon sx={{ fontSize: 16, color: "#495057", mt: 0.25 }} />
                                                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6c757d", minWidth: 80 }}>
                                                                        Cliente:
                                                                    </Typography>
                                                                    <Typography sx={{ fontSize: 13, color: "#212529", flex: 1 }}>
                                                                        {Utils.toCapitalize(order.customer)}
                                                                    </Typography>
                                                                </Box>
                                                            )}

                                                            <Divider />

                                                            {/* Ruta */}
                                                            {order.route && (
                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                    <LocalShippingIcon sx={{ fontSize: 16, color: "#495057" }} />
                                                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6c757d", minWidth: 80 }}>
                                                                        Ruta:
                                                                    </Typography>
                                                                    <Typography sx={{ fontSize: 13, color: "#212529" }}>
                                                                        {Utils.toCapitalize(order.route)}
                                                                    </Typography>
                                                                </Box>
                                                            )}

                                                            {/* Transporte */}
                                                            {order.transport && (
                                                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                                                    <LocalShippingIcon sx={{ fontSize: 16, color: "#495057", mt: 0.25 }} />
                                                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6c757d", minWidth: 80 }}>
                                                                        Transporte:
                                                                    </Typography>
                                                                    <Typography sx={{ fontSize: 13, color: "#212529", flex: 1 }}>
                                                                        {Utils.toCapitalize(order.transport)}
                                                                    </Typography>
                                                                </Box>
                                                            )}

                                                            {/* Nave */}
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                <WarehouseIcon sx={{ fontSize: 16, color: "#495057" }} />
                                                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6c757d", minWidth: 80 }}>
                                                                    Nave:
                                                                </Typography>
                                                                <Typography sx={{ fontSize: 13, color: "#212529" }}>
                                                                    {getNombreAlmacen(order.warehouse)}
                                                                </Typography>
                                                            </Box>

                                                            {/* Estado Nave 1 */}
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6c757d", minWidth: 80, ml: 3 }}>
                                                                    Nave 1:
                                                                </Typography>
                                                                <Chip
                                                                    label={OrderStates.getState(order.stateN1)}
                                                                    size="small"
                                                                    sx={getOrderStateBadgeStyles(order.stateN1, { fontSize: 11, height: 22 })}
                                                                />
                                                            </Box>

                                                            <Divider />

                                                            {/* No preparar */}
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                {order.exclude ? (
                                                                    <CheckBoxIcon sx={{ fontSize: 20, color: "#cc071e" }} />
                                                                ) : (
                                                                    <CheckBoxOutlineBlankIcon sx={{ fontSize: 20, color: "#6c757d" }} />
                                                                )}
                                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: order.exclude ? "#cc071e" : "#495057" }}>
                                                                    No preparar
                                                                </Typography>
                                                            </Box>

                                                            <EtiquetasPedidoN2 pedido={order} productos={productosPorPedido[order.num]} />

                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                startIcon={<DoneAllIcon />}
                                                                onClick={() => forzado.solicitarForzado(order)}
                                                                sx={{
                                                                    textTransform: "none",
                                                                    fontWeight: 600,
                                                                    color: "#495057",
                                                                    borderColor: "#ced4da",
                                                                    backgroundColor: "#fff"
                                                                }}
                                                            >
                                                                Forzar preparado
                                                            </Button>
                                                        </Stack>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })}
                                    </Stack>
                                ) : (
                                    /* Vista desktop: DataGrid */
                                    <Box
                                        sx={{
                                            "& .exclude": {
                                                backgroundColor: "#ff943975 !important",
                                                color: "#1a3e72 !important"
                                            },
                                            "& .include": {
                                                backgroundColor: "#b8daba",
                                                color: "#1a3e72"
                                            }
                                        }}
                                    >
                                        <DataGrid
                                            rows={orders}
                                            columns={columns}
                                            getRowId={(orders) => orders.num}
                                            autoHeight
                                            loading={loading}
                                            slotProps={DATA_GRID_SLOT_PROPS}
                                            sx={DATA_GRID_SX}
                                            rowModesModel={ordersModels}
                                            onRowEditStop={handleRowEditStop}
                                            onRowModesModelChange={handleRowModesModelChange}
                                            processRowUpdate={processRowUpdate}
                                            initialState={{
                                                columns: {
                                                    columnVisibilityModel: {
                                                        priority: false
                                                    }
                                                },
                                                sorting: {
                                                    sortModel: [{ field: "date", sort: "desc" }]
                                                }
                                            }}
                                            getRowClassName={(params) => {
                                                let className = "";
                                                if (params.row.exclude) {
                                                    className += "exclude ";
                                                }
                                                if (Utils.getTransport(params.row.transport)) {
                                                    className += "include ";
                                                }
                                                return className.trim();
                                            }}
                                            localeText={DATA_GRID_LOCALE_TEXT}
                                            disableRowSelectionOnClick={true}
                                            disableVirtualization={true}
                                        />
                                    </Box>
                                )}
                        </>
                    </CardContent>
                </Card>

                <ModalForzarPedidoN2
                    pedido={forzado.pedidoAForzar}
                    loading={forzado.forzando}
                    onHide={forzado.cancelarForzado}
                    onConfirm={forzado.confirmarForzado}
                />
            </Box>
    );
};

export default withRouter(SalesN2);